import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Heart, ShoppingBag, ArrowRight, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

export default function WishlistPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const wishlistCtx = useWishlist?.() || {};
  const { toggleWishlist } = wishlistCtx;
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const id = language === 'id';

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);

  const resolveImg = (raw) => {
    if (!raw) return 'https://images.unsplash.com/photo-1551468220-0a25172193f9?w=400&q=80';
    if (raw.startsWith('http')) return raw;
    return `${BACKEND}${raw}`;
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/wishlist`, { withCredentials: true })
      .then(r => setProducts(r.data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [user]);

  const handleRemove = async (productId) => {
    try {
      if (toggleWishlist) {
        await toggleWishlist(productId);
      } else {
        await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
      }
      setProducts((prev) => prev.filter((p) => p.product_id !== productId));
      toast.success(id ? 'Dihapus dari wishlist' : 'Removed from wishlist');
    } catch {
      toast.error(id ? 'Gagal menghapus' : 'Failed to remove');
    }
  };

  const handleAddCart = async (p) => {
    try {
      await addToCart(p.product_id, 1);
      toast.success(id ? 'Ditambahkan ke keranjang' : 'Added to cart');
    } catch {
      toast.error(id ? 'Gagal menambahkan' : 'Failed to add');
    }
  };

  // Login wall
  if (!user) {
    return (
      <ImmersiveLayout>
        <section className="min-h-[80vh] flex items-center justify-center px-6 py-24" data-testid="wishlist-login-prompt">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full glass-dark flex items-center justify-center">
              <Heart size={28} className="text-seikat-pink" strokeWidth={1.4} />
            </div>
            <p className="eyebrow-immersive">Wishlist</p>
            <h1 className="mt-3 font-heading text-4xl text-seikat-bone">
              {id ? <>Simpan rangkaian yang <em>dicintai</em></> : <>Save the weaves you <em>love</em></>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/60 text-sm">
              {id ? 'Masuk untuk melihat favoritmu.' : 'Sign in to see your favorites.'}
            </p>
            <Link to="/login">
              <button className="btn-pill btn-pill--solid mt-8" data-testid="wishlist-login-btn">
                {id ? 'Masuk' : 'Sign In'} <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </section>
      </ImmersiveLayout>
    );
  }

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="wishlist-page">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? 'Tersimpan' : 'Saved'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Bunga yang <em>dicintai</em>.</> : <>The blooms you <em>love</em>.</>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/65">
              {products.length} {id ? 'item tersimpan' : products.length === 1 ? 'item saved' : 'items saved'}
            </p>
          </motion.div>

          {loading ? (
            <div className="mt-20 flex justify-center"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
          ) : products.length === 0 ? (
            <div className="mt-14 glass-dark rounded-2xl p-12 text-center" data-testid="wishlist-empty">
              <Sparkles size={28} className="mx-auto text-seikat-pink/60 mb-5" strokeWidth={1.4} />
              <h2 className="font-heading text-3xl text-seikat-bone">{id ? 'Wishlist masih sunyi' : 'Wishlist is still quiet'}</h2>
              <p className="font-body text-seikat-bone/60 mt-3 max-w-md mx-auto">
                {id ? 'Tekan tombol hati pada rangkaian yang membuatmu berhenti.' : 'Tap the heart on the weaves that make you pause.'}
              </p>
              <Link to="/catalog">
                <button className="btn-pill btn-pill--solid mt-8" data-testid="wishlist-browse-btn">
                  <ShoppingBag size={14} /> {id ? 'Jelajahi Bouquet' : 'Browse Bouquets'}
                </button>
              </Link>
            </div>
          ) : (
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {products.map((p, i) => (
                  <motion.div
                    key={p.product_id}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
                    className="group"
                    data-testid={`wishlist-item-${p.product_id}`}
                  >
                    <div className="relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer" onClick={() => navigate(`/product/${p.product_id}`)}>
                      <img
                        src={resolveImg(p.image_url || p.images?.[0])}
                        alt={p.name_en || p.name}
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/80 via-transparent to-transparent" />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemove(p.product_id); }}
                        className="absolute top-3 right-3 w-9 h-9 rounded-full glass-dark flex items-center justify-center text-seikat-pink hover:bg-seikat-pink hover:text-seikat-ink transition-colors"
                        aria-label="Remove from wishlist"
                        data-testid={`wishlist-remove-${p.product_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="pt-5 pb-2">
                      <p className="eyebrow-immersive mb-2">{p.occasion_tags?.[0] || p.occasion?.[0] || p.style_tags?.[0] || 'Signature'}</p>
                      <h3 className="font-heading text-2xl text-seikat-bone mb-1 line-clamp-1 cursor-pointer hover:text-seikat-pink transition-colors" onClick={() => navigate(`/product/${p.product_id}`)}>
                        {id ? (p.name_id || p.name) : (p.name_en || p.name)}
                      </h3>
                      <div className="flex items-center justify-between mt-2">
                        <p className="font-body text-sm text-seikat-pink">Rp {fmt(p.price)}</p>
                        <button
                          onClick={() => handleAddCart(p)}
                          className="text-xs tracking-widest uppercase text-seikat-bone-muted hover:text-seikat-lime transition-colors"
                          data-testid={`wishlist-add-cart-${p.product_id}`}
                        >
                          + Cart
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>
    </ImmersiveLayout>
  );
}
